import {
    calculateEntryMetrics,
    calculateMealMetrics,
    getRoundedCalculations,
    getRoundedQuantity,
} from "@client/calculations";
import {Gradient, Headline, SceneTitle} from "@client/components/Chrome";
import {NutritionCard} from "@client/components/NutritionCard";
import {useMealEditor} from "@client/components/Stage";
import {HAIRLINE, INK, JOY_GRADIENT_TOP} from "@client/components/styles";
import * as journal from "@client/features/journal";
import {shareLink} from "@client/features/sharing";
import * as router from "@client/router";
import {classNames, server} from "@reactivated";
import {produce} from "immer";
import * as React from "react";

import {buildCalendar} from "./Journal";

export const UpdateMeal = router.UpdateMeal.withHooks((props) => {
    const mealEditor = useMealEditor({
        target: {
            type: "DAY",
            day: props.resolved.journal.day,
            currentMeal: {
                name: props.resolved.update_meal.name,
                type: props.resolved.update_meal.type,
                hour: props.resolved.update_meal.hour,
            },
        },
        method: "now",
    });

    return {
        ...buildCalendar(props.resolved.journal.day),
        mealEditor,
    };
})
    .withIndex((props) => {
        const meal = props.resolved.update_meal;
        const day = props.resolved.journal.day;
        return (
            <Gradient>
                <div className={classNames("flex flex-col", INK)}>
                    <Headline name={meal.name} />
                    <div className="mt-7">{props.mealEditor.mealSelector}</div>
                    {meal.entries.length > 0 && (
                        <div className="mt-[22px] rounded-[14px] bg-white px-4 py-1">
                            {meal.entries.map((entry) => (
                                <router.Link
                                    key={entry.uuid}
                                    route="update_journal_entry.macros"
                                    params={{key: day.key, entryUUID: entry.uuid}}
                                    className={classNames(
                                        "flex items-center gap-2.5 border-b py-2 last:border-b-0",
                                        HAIRLINE,
                                    )}
                                >
                                    <span className="flex-1 truncate text-[13.5px] font-normal">
                                        {entry.name}
                                        <span className="text-[11.5px] text-[oklch(0.55_0.03_280)]">
                                            {" · "}
                                            {getRoundedQuantity(entry.quantity)}{" "}
                                            {entry.unit}
                                        </span>
                                    </span>
                                    {props.profile.has_hidden_macros === false && (
                                        <span className="text-[14px] font-light">
                                            {getRoundedCalculations(
                                                calculateEntryMetrics({
                                                    entry,
                                                    quantity: entry.quantity,
                                                    unit: entry.unit,
                                                }),
                                                props.profile.use_kilojoules,
                                            ).calories ?? "0"}
                                        </span>
                                    )}
                                    <span className="text-[13px] text-[oklch(0.65_0.02_280)]">
                                        ›
                                    </span>
                                </router.Link>
                            ))}
                        </div>
                    )}
                    {props.profile.has_hidden_macros === false && (
                        <div className="mt-2.5">
                            <NutritionCard
                                calculations={getRoundedCalculations(
                                    calculateMealMetrics(meal),
                                    props.profile.use_kilojoules,
                                )}
                                useKilojoules={props.profile.use_kilojoules}
                                eyebrow="this meal"
                            />
                        </div>
                    )}
                </div>
            </Gradient>
        );
    })
    .withActions(() => [])
    .withOptions((props) => {
        const deleteMeal: router.PillAction = {
            icon: "remove",
            title: "Delete",
            tone: "danger",
            onClick: () =>
                props.dispatch(
                    journal.confirmAndDeleteMeal(props.resolved.update_meal),
                ),
        };
        const saveMeal: router.PillAction = {
            icon: "save",
            title: "Save",
            onClick: () => {
                const result = props.mealEditor.validateMeal();

                if (result == false) {
                    return;
                }

                const updatedDay = produce(props.resolved.journal.day, (draft) => {
                    const sourceMealDraft = draft.meals.find(
                        (meal) => meal.uuid === props.resolved.update_meal.uuid,
                    )!;
                    const sourceMealIndex = draft.meals.findIndex(
                        (m) => m.uuid === props.resolved.update_meal.uuid,
                    );
                    const targetMealDraft = draft.meals.find((meal) =>
                        journal.mealMatches(meal, result),
                    );

                    // Check if we're renaming to an existing meal
                    if (
                        targetMealDraft &&
                        targetMealDraft.uuid !== sourceMealDraft.uuid
                    ) {
                        // Move all entries from source meal to existing target meal
                        targetMealDraft.entries.push(...sourceMealDraft.entries);
                        // Remove the source meal
                        draft.meals.splice(sourceMealIndex, 1);
                    } else {
                        // Just rename the meal
                        sourceMealDraft.name = result.name;
                        sourceMealDraft.type = result.type;
                        sourceMealDraft.hour = result.hour;

                        // If it's an AUTO_CREATED meal, re-sort by removing and re-adding
                        if (result.type === "AUTO_CREATED") {
                            draft.meals.splice(sourceMealIndex, 1);
                            journal.addMealToDayDraft(draft, sourceMealDraft);
                        }
                    }
                });

                const sourceMealRemoved =
                    updatedDay.meals.find(
                        (m) => m.uuid === props.resolved.update_meal.uuid,
                    ) == null;

                if (sourceMealRemoved) {
                    props.notify("Entries moved to existing meal");
                } else {
                    props.notify("Meal updated");
                }

                void server.journal.day.update_day(
                    {user_id: updatedDay.user_id, key: updatedDay.key},
                    {
                        ...updatedDay,
                        deleted_meal_uuids: sourceMealRemoved
                            ? [props.resolved.update_meal.uuid]
                            : [],
                    },
                );

                void router.push(
                    "journal.journal",
                    {key: updatedDay.key},
                    {journal: {...props.resolved.journal, day: updatedDay}},
                );
            },
        };
        const copyMeal: router.PillAction = {
            icon: "copy",
            title: "Copy",
            onClick: () =>
                props.dispatch(journal.pickDateAndCopyMeal(props.resolved.update_meal)),
        };
        const moveMeal: router.PillAction = {
            icon: "scissors",
            title: "Cut",
            onClick: () =>
                props.dispatch(journal.pickDateAndMoveMeal(props.resolved.update_meal)),
        };
        const createRecipe: router.PillAction = {
            icon: "compress",
            title: "Create Recipe",
            onClick: () =>
                router.push("create_recipe_from_meal.index", {
                    key: props.resolved.journal.day.key,
                    mealUUID: props.resolved.update_meal.uuid,
                }),
        };
        const shareMeal: router.PillAction = {
            icon: "share",
            title: "Share",
            onClick: async () => {
                const result = await server.journal.nutrition.publish_meal({
                    meal_uuid: props.resolved.update_meal.uuid,
                });
                if (result.type === "success") {
                    const outcome = await shareLink(
                        props.resolved.update_meal.share_uuid,
                        props.resolved.update_meal.name,
                    );
                    if (outcome === "copied") {
                        props.notify("Link copied.");
                    }
                }
            },
        };

        return {
            isDelegatable: true,
            title: "Update Meal",
            titleSection: <SceneTitle>Update meal</SceneTitle>,
            back: "Journal",
            scene: {
                kind: "SIMPLE",
                width: "column",
                surface: {kind: "custom", background: JOY_GRADIENT_TOP},
                bottom: {
                    kind: "pills",
                    primary: saveMeal,
                    tiles:
                        props.calendar == null
                            ? [deleteMeal, createRecipe]
                            : [deleteMeal, copyMeal, moveMeal, createRecipe, shareMeal],
                },
                corner: props.mealEditor.mealModeToggle,
            },
        };
    });
